// "Fraction entry" questions (2026-10-13): the student types one fraction or
// mixed number -- a whole number if there is one, a top number and a bottom
// number. An answer is sent, and its answer key is stored, as ONE string,
// "f=<fraction>" (for example "f=3/4", "f=1 4/10"), and the two are marked by
// comparing those strings. So both sides must write a fraction the same way:
// this file is the one place that decides how.
//
// Two ways of marking, set by the question's `accept`:
// - 'exact': the form typed is the answer. "6/8" stays "6/8", and
//   "1 4/10" stays "1 4/10".
// - 'equivalent': the value is the answer, in any form. Everything becomes
//   an improper fraction in lowest terms: "2/4" is "1/2", "1 1/2" is "3/2".
//
// Negative fractions (2 Nov 2026, Year 7): the same text with a "-" in
// front, "f=-3/4", "f=-1 1/2" (exact) or "f=-3/2" (equivalent). Zero is
// never negative. Only a question whose content has `negatives: true` shows
// the minus-sign button (see readFractionNegatives).

export type FractionAccept = 'exact' | 'equivalent';

// Each number is whole, from 0 to 999.
const FRACTION_MAX = 999;

// The question's `accept`. Missing, or anything else, is 'exact'.
export function readFractionAccept(content: unknown): FractionAccept {
  if (typeof content !== 'object' || content === null) return 'exact';
  return (content as Record<string, unknown>).accept === 'equivalent'
    ? 'equivalent'
    : 'exact';
}

// Whether the question shows the minus-sign button, so a negative fraction
// can be given. Missing, or anything but true, is no.
export function readFractionNegatives(content: unknown): boolean {
  if (typeof content !== 'object' || content === null) return false;
  return (content as Record<string, unknown>).negatives === true;
}

// A fraction-entry question, told by its content: it has an `accept` and
// none of the lists the other formats have. Never tell by the shape of the
// stored string alone.
export function isFractionContent(content: unknown): boolean {
  if (typeof content !== 'object' || content === null) return false;
  const record = content as Record<string, unknown>;
  return (
    typeof record.accept === 'string' &&
    record.boxes === undefined &&
    record.groups === undefined &&
    record.items === undefined
  );
}

// One typed number: digits only, 0 to 999. "03" is 3. Null for anything else.
function readPart(text: string): number | null {
  if (!/^\d+$/.test(text)) return null;
  const value = Number(text);
  return value <= FRACTION_MAX ? value : null;
}

function greatestCommonDivisor(a: number, b: number): number {
  return b === 0 ? a : greatestCommonDivisor(b, a % b);
}

// The one agreed text for a fraction, from what was typed in the three boxes
// (the whole-number box may be empty) and the minus-sign button. Null until
// the top and the bottom are both numbers, and for a bottom number of 0.
export function canonicalFraction(
  typed: {
    whole: string;
    numerator: string;
    denominator: string;
    negative?: boolean;
  },
  accept: FractionAccept,
): string | null {
  const whole = typed.whole === '' ? 0 : readPart(typed.whole);
  const numerator = readPart(typed.numerator);
  const denominator = readPart(typed.denominator);
  if (whole === null || numerator === null || denominator === null) return null;
  if (denominator === 0) return null;
  // Zero is never negative: "-0/4" is "0/4".
  const sign = typed.negative === true && whole + numerator > 0 ? '-' : '';
  if (accept === 'equivalent') {
    const top = whole * denominator + numerator;
    const divisor = greatestCommonDivisor(top, denominator);
    return `${sign}${String(top / divisor)}/${String(denominator / divisor)}`;
  }
  const fraction = `${String(numerator)}/${String(denominator)}`;
  return sign + (whole > 0 ? `${String(whole)} ${fraction}` : fraction);
}

// The same text from an answer key's numbers, for whatever loads the
// questions. It goes through canonicalFraction, so a key and a typed answer
// can never be written two different ways.
//
// A negative key is written one of three ways: `negative: true` with
// numbers of 0 or more; a negative whole number (whole -1, numerator 1,
// denominator 2 is -1 1/2); or, with no whole number, a negative numerator
// (-3 over 4 is -3/4). Two minus signs, a negative numerator after a whole
// number, or a negative denominator are not a usable key.
export function fractionKey(
  key: {
    whole?: number | null;
    numerator: number;
    denominator: number;
    negative?: boolean | null;
  },
  accept: FractionAccept,
): string | null {
  const whole = key.whole ?? 0;
  const parts = [whole, key.numerator, key.denominator];
  if (!parts.every((part) => Number.isInteger(part))) return null;
  if (key.denominator < 0) return null;
  if (key.numerator < 0 && whole !== 0) return null;
  const minusSigns =
    (key.negative === true ? 1 : 0) +
    (whole < 0 ? 1 : 0) +
    (key.numerator < 0 ? 1 : 0);
  if (minusSigns > 1) return null;
  return canonicalFraction(
    {
      whole: String(Math.abs(whole)),
      numerator: String(Math.abs(key.numerator)),
      denominator: String(key.denominator),
      negative: minusSigns === 1,
    },
    accept,
  );
}

// True when an answer key, as fractionKey writes it, is a negative fraction.
export function fractionKeyIsNegative(key: string): boolean {
  return key.startsWith('-');
}

// What is sent and stored: "f=3/4".
export function fractionEntry(canonical: string): string {
  return `f=${canonical}`;
}

// The other way: "f=1 4/10" back into its fraction text, "1 4/10" (and
// "f=-3/4" into "-3/4"). Null for anything else (a several-boxes or sort
// entry, a tick-all id, for example).
export function parseFractionEntry(entry: unknown): string | null {
  if (typeof entry !== 'string') return null;
  const match = /^f=(-?(?:\d+ )?\d+\/\d+)$/.exec(entry);
  return match?.[1] ?? null;
}
